/**
 * E-GTCC-VIEW-DPLANE1 — the reference building blocks this view needs on
 * top of the shared ones.
 *
 * The Shared UI Foundation Rule (AGENTS.md) applies: `.dot`, `.row`,
 * `.fresh`, `.mono`, "nicht messbar" and the panel heading already exist as
 * `views/cockpit/components/FleetPrimitives.tsx` and are re-used verbatim
 * rather than re-implemented. What lives here is only what the cockpit
 * never needed: the `.panel` wrapper and the failure gate over `DplaneResult`.
 *
 * E-GTCC-VIEW-DPLANE-GW-HW1: die Tabellen- und Zellenvorlagen stehen nicht mehr
 * mit eigenen Werten hier. `TABLE_STYLE`, `TABLE_HEAD_STYLE` und
 * `TABLE_CELL_STYLE` setzen auf `TABLE_STYLE` / `TH_STYLE` / `TD_STYLE` aus
 * `views/control/ControlPrimitives.tsx` auf und legen nur das dazu, was die
 * Referenz zusaetzlich schreibt (Mindestbreite, Unterkante). Die Aktionen der
 * Zeilen sind `ControlTextAction`; `ROW_ACTION_STYLE` haelt darum nur noch den
 * Fluss und keine Knopf-Optik mehr.
 */
import type { CSSProperties, ReactNode } from "react";
import { Button, Card, ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import type { UseQueryResult } from "@tanstack/react-query";

import type { DplaneResult } from "../../../api/dplane.js";
import { PANEL_HEADING_STYLE } from "../../cockpit/components/FleetPrimitives.js";
import {
  TABLE_STYLE as CONTROL_TABLE_STYLE,
  TD_STYLE,
  TH_STYLE,
} from "../../control/ControlPrimitives.js";

/** The three plan builders plus the history, as the Health Center links them. */
export const DPLANE_BUILDER_IDS = ["create", "assign", "move", "history"] as const;
export type DplaneBuilderId = (typeof DPLANE_BUILDER_IDS)[number];

/** What every tab of this view receives from `DplaneView`. */
export interface DplaneTabProps {
  readonly direction: "ltr" | "rtl";
  /** Jump to the Plan Builders tab, optionally with a tenant pre-filled. */
  readonly onOpenPlanBuilder: (builder: DplaneBuilderId, tenantId?: string) => void;
}

/** Query policy of the whole view — identical to the cockpit's. */
export const DPLANE_QUERY_OPTIONS = {
  staleTime: 30_000,
  refetchInterval: 60_000,
  refetchOnWindowFocus: false,
} as const;

/**
 * `.grid-main` — the two-column body of the reference.
 *
 * E-GTCC-DPLANE-TEN-1ZU1 — das Spaltenverhaeltnis ist `1.9fr / 1fr`, nicht
 * `2fr / 1fr` (`GTCC_REFERENCE_2026-09-01.html:86`, uebernommen in
 * `gtcc/ui/cockpit-ui.css:189-193`). Die 15 px, die `2fr` der rechten Spalte
 * nimmt, sind die Ursache der Ellipse in den Inventar-Zeilen: dort steht der
 * laengste Mono-Wert der ganzen View. Darum hier das Verhaeltnis der
 * Referenz statt einer Sonderregel an der Zeile.
 */
export const GRID_MAIN_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.9fr) minmax(0, 1fr)",
  gap: "var(--gt-sys-space-3)",
  alignItems: "start",
};

/** The column stack inside `.grid-main`. */
export const GRID_COLUMN_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--gt-sys-space-3)",
  minWidth: 0,
};

/** `.tabs` of the reference — `font-size: 12px`. */
export const TAB_BAR_STYLE: CSSProperties = { fontSize: "var(--gt-sys-font-size-xs)" };

/** `.sub` — the muted sentence under a head or under a table. */
export const SUB_STYLE: CSSProperties = {
  margin: 0,
  fontSize: "var(--gt-sys-font-size-sm)",
  color: "var(--gt-sys-text-muted)",
};

/** `.mono muted` — the grey monospace addition inside an `h2`. */
export const HEADING_NOTE_STYLE: CSSProperties = {
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  fontWeight: "var(--gt-sys-font-weight-regular)",
  color: "var(--gt-sys-text-muted)",
};

/** `.mono` — a monospace value cell. */
export const MONO_STYLE: CSSProperties = {
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-text)",
};

/** `.host` — the emphasised first cell of a row. */
export const HOST_STYLE: CSSProperties = {
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  color: "var(--gt-sys-text)",
};

/**
 * `table` of the reference — `min-width: 640px` inside the scrolling
 * `.tbl-wrap` (`gtcc/ui/cockpit-ui.css:245-251`). Without the floor the
 * columns squeeze until every cell wraps over two lines; the reference
 * instead lets the wrapper scroll and keeps one line per cell.
 */
/* A-GTCC-ROWGRID1: Schrift, Kopf- und Zellenmasse kommen aus den kanonischen
   `ControlPrimitives` (Ursache 2 des Abgleichs A-GTCC-ACCEPT-ALL2). Lokal
   bleibt nur, was die Referenz hier zusaetzlich verlangt: die 640-px-Untergrenze
   und die Trennlinie unter jeder Zeile. */
export const TABLE_STYLE: CSSProperties = {
  ...CONTROL_TABLE_STYLE,
  minWidth: "640px",
};

/*
 * E-GTCC-VIEW-DPLANE-GW-HW1, Ursache 2 (Zeilenraster), kam unabhaengig zum
 * selben Ergebnis wie A-GTCC-ROWGRID1 und uebernimmt dessen Fassung. Zuvor
 * trugen die beiden Vorlagen hier eigene Werte: Kopf- und Zellenpolsterung je
 * `space-2` (8 px) gegen 4 px (`th`) und 7 px (`td`) der Referenz
 * (`GTCC_REFERENCE_2026-09-01.html:102-103`), der Kopf in halbfett statt fett
 * und ohne die Laufweite `.1em`. Gemessen ergab das im Ist ein Zeilenraster von
 * 40 px gegen 28 px der Referenz. Was oben draufkommt, ist allein die
 * Unterkante, die `td` und `th` der Referenz tragen und die die geteilten
 * Konstanten nicht fuehren (Rest 1 in `E-GTCC-VIEW-LAUNCH-OVW1/ABGLEICH.md`) —
 * kein eigenes Mass.
 */
const BOTTOM_RULE: CSSProperties = { borderBlockEnd: "1px solid var(--gt-sys-border)" };

export const TABLE_HEAD_STYLE: CSSProperties = { ...TH_STYLE, ...BOTTOM_RULE };

export const TABLE_CELL_STYLE: CSSProperties = {
  ...TD_STYLE,
  ...BOTTOM_RULE,
  textAlign: "start",
};

/** `.tbl-wrap` — the horizontally scrollable table box. */
export const TABLE_WRAP_STYLE: CSSProperties = { overflowX: "auto", minWidth: 0 };

/**
 * The label of a `.row`. The reference writes dot, label and mono value on
 * one line and lets the value wrap; without this the label itself breaks as
 * soon as the panel column gets narrow.
 */
export const ROW_LABEL_STYLE: CSSProperties = { whiteSpace: "nowrap" };

/**
 * A-GTCC-FIX-DPLANE1 — die Zeilen der Referenz sind einzeilig: Punkt, Name
 * links, Wert (und die eckige Schaltflaeche) rechts, alles auf einer Hoehe
 * (`GTCC_REFERENCE_2026-09-01.html:934-953`).
 *
 * `.row` ist eine Flexbox, und ein Flex-Element bekommt als Mindestbreite
 * standardmaessig seine Inhaltsbreite. Ohne `minWidth: 0` kann der Wert
 * darum nicht schrumpfen: in der schmalen rechten Tafelspalte des
 * eingebetteten GTCC-Rahmens bricht er stattdessen auf zwei bis drei Zeilen
 * um. Diese drei Vorlagen bilden die Zeile an der Aufrufstelle nach —
 * `Row` (`ui/primitives.tsx`) bleibt die gemeinsame Primitive fuer `.row`
 * und den Punkt, genau wie `InfraTab.tsx` es haelt.
 *
 * Der Wert wird nur sichtbar beschnitten (Ellipse); der volle Text bleibt im
 * DOM und damit fuer Screenreader und die Tests vollstaendig lesbar. Bei der
 * Breite der Referenz wird nichts beschnitten.
 */
export const ROW_LINE_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-3)",
  flex: "1 1 auto",
  minWidth: 0,
};

/** Die rechte Haelfte einer Zeile: Wert und Schaltflaeche nebeneinander. */
export const ROW_VALUE_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  flexWrap: "nowrap",
  gap: "var(--gt-sys-space-2)",
  marginInlineStart: "auto",
  minWidth: 0,
};

/** Der Text in der rechten Haelfte: eine Zeile, notfalls mit Ellipse. */
export const ROW_VALUE_TEXT_STYLE: CSSProperties = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/**
 * Der Platz der Aktion in einer Zeile: sie schrumpft nie und bricht nie um.
 *
 * E-GTCC-VIEW-DPLANE-GW-HW1, Ursache 2 (Zeilenraster): die Vorlage hat vorher
 * zusaetzlich Schriftart, Schriftgrad und Polsterung eines `Button` des
 * Design-Systems nachgestellt. Dessen Mindesthoehe von 32 px blieb davon
 * unberuehrt und hat die Zeile auf 44 px getrieben, wo die Referenz 31 px setzt.
 * Die Aktion selbst ist jetzt `ControlTextAction` aus
 * `views/control/ControlPrimitives.tsx` — genau der `.mono`-Text, den die
 * Referenz in den Zeilen 935 und 950-953 schreibt. Hier bleibt nur der Fluss.
 */
export const ROW_ACTION_STYLE: CSSProperties = {
  flex: "0 0 auto",
  display: "inline-flex",
  whiteSpace: "nowrap",
};

/*
 * Hier stand `TABLE_ACTION_STYLE`: eine lokale Vorlage, die einen `Button` des
 * Design-Systems auf die Schriftart der Referenz gezogen hat. Sie ist mit
 * E-GTCC-VIEW-DPLANE-GW-HW1 entfallen. Die Mindesthoehe des Knopfs blieb davon
 * unberuehrt und hat das Zeilenraster gesprengt; die UMZUG-Zelle nimmt jetzt
 * `ControlTextAction` aus `views/control/ControlPrimitives.tsx`.
 */

/**
 * Eine Liste aus einer Antwort, die im Typ eine Liste ist, es zur Laufzeit
 * aber nicht sein muss. Schickt die Quelle `null`, ein Objekt oder gar nichts,
 * ist das ein Fehler der Quelle; die View zeigt dann den gemessenen
 * Leerzustand ("nicht messbar") statt zu stuerzen (Owner-Entscheid P10: nie
 * eine erfundene Zahl, aber auch nie ein Absturz statt einer Aussage).
 */
export function asList<T>(value: ReadonlyArray<T> | null | undefined): ReadonlyArray<T> {
  return Array.isArray(value) ? value : [];
}

export function Mono(props: {
  children: ReactNode;
  testId?: string;
  /** Zusatz-Stil der Aufrufstelle, zum Beispiel die einzeilige Ellipse. */
  style?: CSSProperties | undefined;
}): ReactNode {
  return (
    <span data-testid={props.testId} style={{ ...MONO_STYLE, ...props.style }}>
      {props.children}
    </span>
  );
}

/**
 * `.panel` — a card with an `h3` head. The reference writes `h2`; inside a
 * GTCC view the shell already owns the page `h1` and the view owns exactly
 * one level-1/2 heading (`gtcc-a11y-smoke.test.tsx:287`), so a panel head is
 * an `h3` carrying the reference's panel type scale.
 */
export function DplanePanel(props: {
  title: string;
  note?: ReactNode;
  testId: string;
  children: ReactNode;
}): ReactNode {
  /*
   * `section` mit `aria-labelledby` statt eines blanken `div`: jede Tafel
   * traegt eine eigene Ueberschrift, und die Bedienelemente in ihr (vor
   * allem die gleichlautende Wiederholen-Schaltflaeche der Fehlertafeln)
   * sind nur dann unterscheidbar, wenn die Tafel als benannter Bereich
   * ausgezeichnet ist.
   */
  const headingId = `${props.testId}-title`;
  /*
   * `.panel h2` der Referenz steht auf `justify-content: space-between`
   * (`gtcc/ui/cockpit-ui.css:232-241`): der Titel links, die `.mono muted`
   * Ergaenzung am rechten Rand der Tafel. Die gemeinsame
   * `PANEL_HEADING_STYLE` reiht beides dicht hintereinander, weil das
   * Cockpit dort keine Ergaenzung traegt — darum hier nur diese eine
   * Ausrichtung obendrauf, ohne die gemeinsame Vorlage anzufassen.
   */
  const headingStyle: CSSProperties = { ...PANEL_HEADING_STYLE, justifyContent: "space-between" };
  return (
    <Card elevation={0}>
      <section aria-labelledby={headingId}>
        <Stack gap={2}>
          <h3 id={headingId} style={headingStyle} data-testid={`${props.testId}-title`}>
            {props.title}
            {props.note === undefined ? null : <span style={HEADING_NOTE_STYLE}>{props.note}</span>}
          </h3>
          <div data-testid={props.testId}>{props.children}</div>
        </Stack>
      </section>
    </Card>
  );
}

/**
 * One gate for every read of this view. A pending query renders the loading
 * state, a failure renders a named, visible failure — never an empty panel
 * and never a placeholder number (Owner decision P10).
 *
 * `readDplane()` classifies every expected failure into the envelope, so a
 * rejected query is the unexpected case. It is still a failure and is drawn
 * as one: a query that errored must never keep showing "wird gemessen",
 * because a stale loading label reads as "still measuring" when in truth
 * nothing is being measured any more.
 */
export function DplaneGate<T>(props: {
  query: UseQueryResult<DplaneResult<T>>;
  testId: string;
  render: (data: T) => ReactNode;
}): ReactNode {
  const { t } = useLocale();
  const { query } = props;
  if (query.data === undefined) {
    if (query.isError)
      return (
        <DplaneFailure
          testId={props.testId}
          body={failureBody(t)}
          onRetry={() => void query.refetch()}
        />
      );
    return (
      <div data-testid={`${props.testId}-loading`}>
        {/*
         * `LoadingState` statt `EmptyState`: die gemeinsame Primitive spricht
         * den Ladetext ueber `role="status"` + `aria-live="polite"` an
         * (`LoadingState.tsx:57-66`). Ein leerer Zustand waere hier auch
         * inhaltlich falsch — es ist nichts leer, es wird noch gemessen.
         */}
        <LoadingState label={t("admin.gtcc.dplane.loading_panel")} />
      </div>
    );
  }
  if (query.data.ok) return props.render(query.data.data);
  const failure = query.data;
  const body =
    failure.kind === "disabled"
      ? t("admin.gtcc.dplane.failure.disabled")
      : failure.kind === "forbidden"
        ? t("admin.gtcc.dplane.failure.forbidden")
        : failure.kind === "upstream"
          ? t("admin.gtcc.dplane.failure.upstream", { status: failure.httpStatus })
          : t("admin.gtcc.dplane.failure.transport");
  /*
   * "disabled" und "forbidden" heilt kein erneuter Versuch — dort bleibt die
   * Meldung ohne Schaltflaeche. "upstream" und "transport_error" koennen
   * voruebergehend sein, darum bieten sie den erneuten Versuch an.
   */
  const retryable = failure.kind === "upstream" || failure.kind === "transport_error";
  return (
    <DplaneFailure
      testId={props.testId}
      body={body}
      onRetry={retryable ? () => void query.refetch() : undefined}
    />
  );
}

/** The transport wording, used when the query itself rejected. */
function failureBody(t: (key: string) => string): string {
  return t("admin.gtcc.dplane.failure.transport");
}

function DplaneFailure(props: {
  testId: string;
  body: string;
  /*
   * `exactOptionalPropertyTypes`: "disabled" und "forbidden" reichen hier
   * bewusst `undefined` durch (kein erneuter Versuch), darum gehoert
   * `undefined` in den Typ und nicht nur die Abwesenheit der Eigenschaft.
   */
  onRetry?: (() => void) | undefined;
}): ReactNode {
  const { t } = useLocale();
  return (
    <div data-testid={`${props.testId}-failure`}>
      <ErrorState
        severity="warn"
        title={t("admin.gtcc.dplane.failure.title")}
        body={props.body}
        action={
          props.onRetry === undefined ? undefined : (
            <Button variant="secondary" onClick={props.onRetry}>
              {t("common.retry")}
            </Button>
          )
        }
      />
    </div>
  );
}
